import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';

import { BaseImportStepComponent, ImportStepState } from '../../base-import-step.component';
import { DataService } from 'src/app/shared/network/data.service';
import { ImportProgressService } from 'src/app/shared/import/import-progress.service';
import { ProjectService } from 'src/app/shared/project/project.service';
import { CalcOptionsModel, FormCompVarianceLine, FormulaItemType, RefVolumeBasis } from './reference-settings';
import { VarianceFormulaBaselineType } from './variance-formula-baseline';

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ImportStep } from '../../import-step';

@Component({
    selector: 'calc-options',
    templateUrl: './calc-options.component.html',
    styleUrls: ['../../general.scss', '../column-selection.scss']
})
export class CalcOptionsComponent extends BaseImportStepComponent implements OnInit {
    readonly step = ImportStep.CalcOptions;
    FormulaItemType: typeof FormulaItemType = FormulaItemType;
    VarianceFormulaBaselineType: typeof VarianceFormulaBaselineType = VarianceFormulaBaselineType;
    public baseLineType: VarianceFormulaBaselineType;
    public oldControlValue: string;
    public newControlValue: string;
    public oldBaseLine: FormCompVarianceLine[] | null;
    public newBaseLine: FormCompVarianceLine[] | null;
    public canonical: FormCompVarianceLine[] | null;
    public formCompNames: string[] = ['', ''];
    public tiles: string[] = ['Visits', 'Visits to Bookings', 'Length of Stay', 'Average Daily Rate'];

    loading = false;
    dataLoaded = false;

    constructor(private dataService: DataService, projectService: ProjectService) {
        super(projectService);
    }

    public async loadDataInternal(): Promise<void> {
        const resp = await this.dataService.get<CalcOptionsModel>('columnSelection/CalcOptions/' + this.projectId);
        this.stepState = resp.stepState;
        if (this.stepState < ImportStepState.Available) {
            return;
        }
        this.baseLineType = resp.varianceFormulaBaseLine.type;
        this.oldControlValue = resp.oldControlValue;
        this.newControlValue = resp.newControlValue;
        this.oldBaseLine = resp.oldBaseLine;
        this.newBaseLine = resp.newBaseLine;
        this.canonical = resp.canonical;
        this.formCompNames = resp.oldBaseLine.map(x => x.formCompName);
        this.dataLoaded = true;
    }

    override ngOnInit(): void {
        super.ngOnInit();
    }

    async save() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            
            const changesApplied = await this.dataService.post<boolean>('columnSelection/calcOptions', {
                projectId: this.projectId,
                varianceFormulaBaseLine: {
                    type: this.baseLineType,
                    formCompSeq: this.baseLineType === VarianceFormulaBaselineType.Canonical
                                                   ? this.canonical.map(x => x.formCompIx)
                                                   : null,
                },
            });
            this.finishStepSuccessfully(changesApplied);
            
        } finally {
            this.loading = false;
        }
    }

    public drop(event: CdkDragDrop<FormCompVarianceLine[]>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }
        moveItemInArray(this.canonical, event.previousIndex, event.currentIndex);
        this.reMarkNewAndOld(this.canonical);
    }

    private isNewOrOldFormComp(formCompSeq: number[], varianceFormCompIx: number, curFormCompIx: number, isNewFormulaPart: boolean): boolean {
        const varFormCompSeqIx = formCompSeq.indexOf(varianceFormCompIx);
        const curFormCompSeqIx = formCompSeq.indexOf(curFormCompIx);
        return isNewFormulaPart
                   ? curFormCompSeqIx <= varFormCompSeqIx
                   : curFormCompSeqIx < varFormCompSeqIx;
    }

    private reMarkNewAndOld(canonical: FormCompVarianceLine[]): void {
        let formCompSeq = canonical.map(x => x.formCompIx);
        for (let line of canonical) {
            for (let item of line.newFormulaPart) {
                if (item.type !== FormulaItemType.Other) {
                    item.type = this.isNewOrOldFormComp(formCompSeq, line.formCompIx, item.formCompIx, true)
                                ? FormulaItemType.NewFormComp
                                : FormulaItemType.OldFormComp;
                }
            }
            for (let item of line.oldFormulaPart) {
                if (item.type !== FormulaItemType.Other) {
                    item.type = this.isNewOrOldFormComp(formCompSeq, line.formCompIx, item.formCompIx, false)
                                ? FormulaItemType.NewFormComp
                                : FormulaItemType.OldFormComp;
                }
            }
        }
    }
}
